"use client";

import { c as _c } from "react/compiler-runtime";
import { type HierarchiesControllerGetAncestorsQueryResponse, useHierarchiesControllerGetAncestors } from "@dc/gateway-jsonapi";
import { Badge, Button, Chip, Divider, Link, ScrollShadow, Spacer, Tooltip } from "@dc/ui/components";
import { cn } from "@dc/ui/lib/utils";
import { LeftPanelCloseIcon, LeftPanelOpenIcon, LogoIcon, MenuOpenIcon, Wordmark } from "@dc/ui/vectors";
import { useTranslations } from "next-intl";
import type { FC } from "react";
import { ArrowRightUpLinear, BellLinear, MagniferLinear, MoonLinear, QuestionCircleLinear, ShieldUserLinear, SunLinear } from "solar-icon-set";
import { Can } from "@/components/Ability/Can";
import { TeamSwitcher } from "@/components/TeamSwitcher/TeamSwitcher";
import { useActiveHierarchyScope } from "@/hooks/useActiveHierarchyScope";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useMe } from "@/hooks/useMe";
import { useNavigationUpdatesEnabled } from "@/hooks/useNavigationUpdatesEnabled";
import { useNotifications } from "@/hooks/useNotifications";
import { useProjectsHomeHref } from "@/hooks/useProjectsHomeHref";
import { usePylon } from "@/hooks/usePylon";
import { getDcOneArchiveUrl } from "@/lib/dcOneUrls";
import { useOrgTeamPath } from "@/lib/orgTeamPath";
import { GroupedSidebar } from "../Sidebar/GroupedSidebar";
import { SidebarDrawer } from "../SidebarDrawer/SidebarDrawer";
import { UserDropdown } from "../UserDropdown/UserDropdown";
import type { NavigationSidebarState } from "./NavigationSidebar.hooks";
import styles from "./NavigationSidebar.module.css";
import { SidebarSettingsButton } from "./SidebarSettingsButton/SidebarSettingsButton";
const DEFAULT_ICON_COLOR = "text-zinc-300";
interface NavigationSidebarProps {
  state: NavigationSidebarState;
  showTeamSwitcher?: boolean;
  showSearch?: boolean;
  onNavigate?: (key: string) => void;
  onSearchOpen?: () => void;
  searchShortcutLabel?: string;
}
function selectOrganizationId({
  data
}: HierarchiesControllerGetAncestorsQueryResponse) {
  return data.find(ancestor => ancestor.attributes.type === "ORGANIZATION")?.id;
}
export const NavigationSidebar: FC<NavigationSidebarProps> = t0 => {
  const $ = _c(110);
  const {
    state,
    showTeamSwitcher: t1,
    showSearch: showSearchProp,
    onNavigate,
    onSearchOpen,
    searchShortcutLabel
  } = t0;
  const showTeamSwitcher = t1 === undefined ? true : t1;
  const {
    isCollapsed,
    isMobile,
    isOpen,
    isOrgSettingsActive,
    isTeamSettingsActive,
    isSASettingsActive,
    mounted,
    onClose,
    onOpenChange,
    onToggle,
    selectedKey,
    theme,
    toggleTheme,
    hasOverflow,
    scrollContainerRef,
    sidebarBreakpoint
  } = state;
  const {
    build: buildPath
  } = useOrgTeamPath();
  const flags = useFeatureFlags();
  const navigationUpdatesEnabled = useNavigationUpdatesEnabled();
  const {
    href: projectsHomeLogicalPath
  } = useProjectsHomeHref();
  let t2;
  if ($[0] !== buildPath || $[1] !== projectsHomeLogicalPath) {
    t2 = buildPath(projectsHomeLogicalPath);
    $[0] = buildPath;
    $[1] = projectsHomeLogicalPath;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const logoHref = t2;
  const showSearch = showSearchProp ?? flags["sidebar-show-search"];
  const showThemeToggle = flags["sidebar-show-theme-toggle-ff-84"] ?? true;
  const showGoToDc1Flag = flags["sidebar-show-go-to-dc-1-ff"] ?? true;
  const {
    wasMigratedFromDc1
  } = useMe();
  const showGoToDc1 = mounted && showGoToDc1Flag && wasMigratedFromDc1;
  const {
    scope
  } = useActiveHierarchyScope();
  let t3;
  if ($[3] !== buildPath) {
    t3 = buildPath("/settings/admin/organizations");
    $[3] = buildPath;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const saSettingsHref = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      scopeType: "ORGANIZATION"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = !!scope.scopeId;
  let t6;
  if ($[6] !== t5) {
    t6 = {
      query: {
        enabled: t5,
        select: selectOrganizationId
      }
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const {
    data: organizationId
  } = useHierarchiesControllerGetAncestors(scope.scopeId, t4, t6);
  const {
    toggleChat
  } = usePylon();
  const {
    unreadCount,
    unreadCountLabel,
    toggleDrawer
  } = useNotifications();
  const t = useTranslations("navigation.actions");
  const tShell = useTranslations("navigation.shell");
  const tCommon = useTranslations();
  let t7;
  if ($[8] !== isCollapsed || $[9] !== isMobile) {
    t7 = cn("min-w-[320px] rounded-lg text-zinc-300", {
      "min-w-[83px]": isCollapsed
    }, {
      "bg-navy-800!": isMobile
    });
    $[8] = isCollapsed;
    $[9] = isMobile;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = !isCollapsed && "w-80 px-5";
  const t9 = isCollapsed && "ml-1.5 w-[83px] items-center";
  let t10;
  if ($[11] !== t8 || $[12] !== t9) {
    t10 = cn("relative flex h-full max-h-full shrink-0 flex-col py-3 transition-all duration-300 ease-in-out sm:py-5.5", t8, t9);
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== isCollapsed) {
    t11 = cn("flex shrink-0 items-center justify-center sm:pr-0", {
      "w-12": isCollapsed
    });
    $[14] = isCollapsed;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== isCollapsed || $[17] !== isMobile) {
    t12 = cn("flex w-full items-center justify-between", {
      "flex-wrap": isCollapsed,
      "justify-center gap-1": isCollapsed,
      "justify-start": isMobile
    });
    $[16] = isCollapsed;
    $[17] = isMobile;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t) {
    t13 = t("close_menu");
    $[19] = t;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <MenuOpenIcon />;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== onOpenChange || $[23] !== t13) {
    t15 = <Button size="sm" variant="solid" isIconOnly={true} className="mr-2 bg-transparent text-lg text-white hover:bg-navy-700! sm:hidden" onPress={onOpenChange} aria-label={t13}>{t14}</Button>;
    $[22] = onOpenChange;
    $[23] = t13;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== isCollapsed) {
    t16 = cn("flex flex-1 items-center", {
      "w-full sm:justify-center": isCollapsed
    });
    $[25] = isCollapsed;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== isCollapsed) {
    t17 = isCollapsed ? <LogoIcon className="w-5 text-white" /> : <Wordmark className="w-32 text-white sm:w-36" />;
    $[27] = isCollapsed;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== logoHref || $[30] !== t17) {
    t18 = <Link href={logoHref} className="flex items-center">{t17}</Link>;
    $[29] = logoHref;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t16 || $[33] !== t18) {
    t19 = <div className={t16}>{t18}</div>;
    $[32] = t16;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== isCollapsed || $[36] !== onSearchOpen || $[37] !== searchShortcutLabel || $[38] !== showSearch || $[39] !== tCommon || $[40] !== tShell) {
    t20 = showSearch && <Tooltip content={tShell("search_with_shortcut", {
      shortcut: searchShortcutLabel ?? ""
    })} placement={isCollapsed ? "right" : "bottom"} color="foreground"><button type="button" aria-label={tCommon("search")} className={cn("inline-flex size-8 cursor-pointer items-center justify-center rounded-medium outline-none transition-colors hover:bg-navy-700!", isCollapsed && "mt-2")} onClick={onSearchOpen}><MagniferLinear className="size-5 text-zinc-300!" /></button></Tooltip>;
    $[35] = isCollapsed;
    $[36] = onSearchOpen;
    $[37] = searchShortcutLabel;
    $[38] = showSearch;
    $[39] = tCommon;
    $[40] = tShell;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== isCollapsed || $[43] !== t) {
    t21 = isCollapsed ? t("expand_panel") : t("collapse_panel");
    $[42] = isCollapsed;
    $[43] = t;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = isCollapsed ? "right" : "bottom";
  const t23 = !isCollapsed;
  let t24;
  if ($[45] !== isCollapsed || $[46] !== t23) {
    t24 = cn("hidden text-zinc-300! hover:bg-navy-700! sm:flex", {
      "mt-2 justify-center": isCollapsed,
      "ml-2": t23
    });
    $[45] = isCollapsed;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  const t25 = isMobile ? onOpenChange : onToggle;
  let t26;
  if ($[48] !== isCollapsed || $[49] !== t) {
    t26 = isCollapsed ? t("expand_panel") : t("collapse_panel");
    $[48] = isCollapsed;
    $[49] = t;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== isCollapsed) {
    t27 = isCollapsed ? <LeftPanelOpenIcon className="size-6" /> : <LeftPanelCloseIcon className="size-6" />;
    $[51] = isCollapsed;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t24 || $[54] !== t25 || $[55] !== t26 || $[56] !== t27) {
    t28 = <Button fullWidth={true} size="sm" className={t24} isIconOnly={true} onPress={t25} color="pop" variant="bordered" data-testid="sidebar-collapse-toggle" aria-label={t26}>{t27}</Button>;
    $[53] = t24;
    $[54] = t25;
    $[55] = t26;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t21 || $[59] !== t22 || $[60] !== t28) {
    t29 = <Tooltip content={t21} placement={t22} color="foreground">{t28}</Tooltip>;
    $[58] = t21;
    $[59] = t22;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t12 || $[63] !== t15 || $[64] !== t19 || $[65] !== t20 || $[66] !== t29) {
    t30 = <div className={t12}>{t15}{t19}{t20}{t29}</div>;
    $[62] = t12;
    $[63] = t15;
    $[64] = t19;
    $[65] = t20;
    $[66] = t29;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== t11 || $[69] !== t30) {
    t31 = <div className={t11}>{t30}</div>;
    $[68] = t11;
    $[69] = t30;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== isCollapsed || $[72] !== showTeamSwitcher) {
    t32 = showTeamSwitcher && <div className={cn("w-full", isCollapsed ? "hidden" : "mt-4")}><TeamSwitcher /></div>;
    $[71] = isCollapsed;
    $[72] = showTeamSwitcher;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Spacer y={6} />;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== hasOverflow || $[76] !== isCollapsed || $[77] !== isMobile || $[78] !== isOrgSettingsActive || $[79] !== isSASettingsActive || $[80] !== isTeamSettingsActive || $[81] !== mounted || $[82] !== navigationUpdatesEnabled || $[83] !== onClose || $[84] !== onNavigate || $[85] !== organizationId || $[86] !== saSettingsHref || $[87] !== scrollContainerRef || $[88] !== selectedKey || $[89] !== showGoToDc1 || $[90] !== showThemeToggle || $[91] !== t || $[92] !== theme || $[93] !== toggleChat || $[94] !== toggleDrawer || $[95] !== toggleTheme || $[96] !== unreadCount || $[97] !== unreadCountLabel) {
    t34 = isMobile ? <div className={styles.scrollableContentMobile}><nav className={cn(styles.navContainerMobile, {
        "mt-[5px]": isCollapsed
      })}><GroupedSidebar key={selectedKey} defaultSelectedKey={selectedKey} isCompact={isCollapsed} onSelect={onNavigate} /></nav><div className={cn("flex flex-col", {
        "items-center": isCollapsed
      })}>{!navigationUpdatesEnabled && <Divider className="bg-zinc-300" />}<Spacer y={4} />{!navigationUpdatesEnabled && <><Tooltip content={t("notifications")} isDisabled={!isCollapsed} placement="right" color="foreground"><div className="w-full"><Button fullWidth={true} className={cn("justify-start truncate bg-transparent text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
                "justify-center": isCollapsed
              })} isIconOnly={isCollapsed} onPress={toggleDrawer} startContent={isCollapsed ? null : <BellLinear size={24} className="flex-none text-default-600" />} endContent={isCollapsed || unreadCount === 0 ? null : <Chip color="pop" size="sm" variant="solid" className="min-h-6 min-w-6 flex-none">{unreadCountLabel}</Chip>} data-testid="sidebar-notifications" aria-label={t("notifications")}>{isCollapsed ? <Badge content={unreadCountLabel} color="default" size="sm" className="flex-none border-0 bg-bg-interactive-pop-solid-default text-black!" isInvisible={unreadCount === 0}><BellLinear size={24} className={DEFAULT_ICON_COLOR} /></Badge> : t("notifications")}</Button></div></Tooltip><Tooltip content={t("help_and_resources")} isDisabled={!isCollapsed} placement="right" color="foreground"><div className="w-full"><Button fullWidth={true} className={cn("justify-start truncate bg-transparent text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
                "justify-center": isCollapsed
              })} isIconOnly={isCollapsed} onPress={toggleChat} startContent={isCollapsed ? null : <QuestionCircleLinear size={24} className={cn("flex-none", DEFAULT_ICON_COLOR)} />} data-testid="sidebar-help" aria-label={t("help_and_resources")}>{isCollapsed ? <QuestionCircleLinear size={24} className="text-default-500" /> : t("help_and_resources")}</Button></div></Tooltip><SidebarSettingsButton organizationId={organizationId || undefined} isCollapsed={isCollapsed} isOrgSettingsActive={isOrgSettingsActive} isTeamSettingsActive={isTeamSettingsActive} onPress={onClose} variant="mobile" /><Can action="manage" resource="SYSTEM"><Tooltip content={t("system_admin")} isDisabled={!isCollapsed} placement="right" color="foreground"><div className="w-full"><Button fullWidth={true} className={cn("justify-start truncate bg-transparent text-sm text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
                  "justify-center": isCollapsed,
                  "bg-navy-900! font-medium": isSASettingsActive
                })} isIconOnly={isCollapsed} as={Link} href={saSettingsHref} onPress={onClose} startContent={isCollapsed ? null : <ShieldUserLinear className={cn("size-6", isSASettingsActive ? "text-default-900" : "")} />} data-testid="sidebar-system-admin" aria-label={t("system_admin")}>{isCollapsed ? <ShieldUserLinear className={cn("size-6", isSASettingsActive ? "text-default-900" : "text-default-500")} /> : t("system_admin")}</Button></div></Tooltip></Can>{mounted && showThemeToggle && <Tooltip content={theme === "dark" ? t("light_mode") : t("dark_mode")} isDisabled={!isCollapsed} placement="right" color="foreground"><div className="w-full"><Button fullWidth={true} className={cn("justify-start truncate bg-transparent text-sm text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
                "justify-center": isCollapsed
              })} isIconOnly={isCollapsed} onPress={toggleTheme} startContent={isCollapsed ? null : theme === "dark" ? <SunLinear size={24} className={cn("flex-none", DEFAULT_ICON_COLOR)} /> : <MoonLinear size={24} className={cn("flex-none", DEFAULT_ICON_COLOR)} />} data-testid="sidebar-theme-toggle" aria-label={theme === "dark" ? t("light_mode") : t("dark_mode")}>{isCollapsed ? theme === "dark" ? <SunLinear size={24} className={DEFAULT_ICON_COLOR} /> : <MoonLinear size={24} className={DEFAULT_ICON_COLOR} /> : theme === "dark" ? t("light_mode") : t("dark_mode")}</Button></div></Tooltip>}{showGoToDc1 && <Tooltip content={t("go_to_old_platform")} isDisabled={!isCollapsed} placement="right" color="foreground"><div className="w-full"><Button fullWidth={true} className={cn("justify-start truncate bg-transparent text-sm text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
                "justify-center": isCollapsed
              })} isIconOnly={isCollapsed} onPress={_temp} startContent={isCollapsed ? null : <ArrowRightUpLinear size={24} className={cn("flex-none", DEFAULT_ICON_COLOR)} />} data-testid="sidebar-go-to-dc1" aria-label={t("go_to_old_platform")}>{isCollapsed ? <ArrowRightUpLinear size={24} className={DEFAULT_ICON_COLOR} /> : t("go_to_old_platform")}</Button></div></Tooltip>}</>}<UserDropdown isCollapsed={isCollapsed} /></div></div> : <><nav className={cn("relative w-full flex-1 overflow-y-auto", styles.navContainerExpanded, {
        "mt-[5px]": isCollapsed
      })}><ScrollShadow ref={scrollContainerRef} className={cn("absolute inset-0 h-full", {
          [styles.scrollShadowCollapsed]: isCollapsed && hasOverflow
        })}><GroupedSidebar key={selectedKey} defaultSelectedKey={selectedKey} isCompact={isCollapsed} onSelect={onNavigate} /></ScrollShadow></nav><div className={cn("mt-auto flex flex-col", {
        "items-center": isCollapsed
      })}>{!navigationUpdatesEnabled && <Divider className="bg-zinc-300" />}<Spacer y={4} />{!navigationUpdatesEnabled && <><Tooltip content={t("notifications")} isDisabled={!isCollapsed} placement="right" color="foreground"><div className="w-full"><Button fullWidth={true} className={cn("justify-start truncate bg-transparent text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
                "justify-center": isCollapsed
              })} isIconOnly={isCollapsed} onPress={toggleDrawer} startContent={isCollapsed ? null : <BellLinear size={24} className="flex-none text-default-600" />} endContent={isCollapsed || unreadCount === 0 ? null : <Chip color="pop" size="sm" variant="solid" className="min-h-6 min-w-6 flex-none">{unreadCountLabel}</Chip>} data-testid="sidebar-notifications" aria-label={t("notifications")}>{isCollapsed ? <Badge content={unreadCountLabel} color="default" size="sm" className="flex-none border-0 bg-bg-interactive-pop-solid-default text-black!" isInvisible={unreadCount === 0}><BellLinear size={24} className={DEFAULT_ICON_COLOR} /></Badge> : t("notifications")}</Button></div></Tooltip><Tooltip content={t("help_and_resources")} isDisabled={!isCollapsed} placement="right" color="foreground"><div className="w-full"><Button fullWidth={true} className={cn("justify-start truncate bg-transparent text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
                "justify-center": isCollapsed
              })} isIconOnly={isCollapsed} onPress={toggleChat} startContent={isCollapsed ? null : <QuestionCircleLinear size={24} className={cn("flex-none", DEFAULT_ICON_COLOR)} />} data-testid="sidebar-help" aria-label={t("help_and_resources")}>{isCollapsed ? <QuestionCircleLinear size={24} className="text-default-500" /> : t("help_and_resources")}</Button></div></Tooltip><SidebarSettingsButton organizationId={organizationId || undefined} isCollapsed={isCollapsed} isOrgSettingsActive={isOrgSettingsActive} isTeamSettingsActive={isTeamSettingsActive} variant="desktop" /><Can action="manage" resource="SYSTEM"><Tooltip content={t("system_admin")} isDisabled={!isCollapsed} placement="right" color="foreground"><div className="w-full"><Button fullWidth={true} className={cn("justify-start truncate bg-transparent text-sm text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
                  "justify-center": isCollapsed,
                  "bg-navy-900": isSASettingsActive
                })} isIconOnly={isCollapsed} as={Link} href={saSettingsHref} startContent={isCollapsed ? null : <ShieldUserLinear className="size-6" />} data-testid="sidebar-system-admin" aria-label={t("system_admin")}>{isCollapsed ? <ShieldUserLinear className="size-6 text-default-500" /> : t("system_admin")}</Button></div></Tooltip></Can>{mounted && showThemeToggle && <Tooltip content={theme === "dark" ? t("light_mode") : t("dark_mode")} isDisabled={!isCollapsed} placement="right" color="foreground"><div className="w-full"><Button fullWidth={true} className={cn("justify-start truncate bg-transparent text-sm text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
                "justify-center": isCollapsed
              })} isIconOnly={isCollapsed} onPress={toggleTheme} startContent={isCollapsed ? null : theme === "dark" ? <SunLinear size={24} className={cn("flex-none", DEFAULT_ICON_COLOR)} /> : <MoonLinear size={24} className={cn("flex-none", DEFAULT_ICON_COLOR)} />} data-testid="sidebar-theme-toggle" aria-label={theme === "dark" ? t("light_mode") : t("dark_mode")}>{isCollapsed ? theme === "dark" ? <SunLinear size={24} className={DEFAULT_ICON_COLOR} /> : <MoonLinear size={24} className={DEFAULT_ICON_COLOR} /> : theme === "dark" ? t("light_mode") : t("dark_mode")}</Button></div></Tooltip>}{showGoToDc1 && <Tooltip content={t("go_to_old_platform")} isDisabled={!isCollapsed} placement="right" color="foreground"><div className="w-full"><Button fullWidth={true} className={cn("justify-start truncate bg-transparent text-sm text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
                "justify-center": isCollapsed
              })} isIconOnly={isCollapsed} onPress={_temp2} startContent={isCollapsed ? null : <ArrowRightUpLinear size={24} className={cn("flex-none", DEFAULT_ICON_COLOR)} />} data-testid="sidebar-go-to-dc1" aria-label={t("go_to_old_platform")}>{isCollapsed ? <ArrowRightUpLinear size={24} className={DEFAULT_ICON_COLOR} /> : t("go_to_old_platform")}</Button></div></Tooltip>}</>}<UserDropdown isCollapsed={isCollapsed} /></div></>;
    $[75] = hasOverflow;
    $[76] = isCollapsed;
    $[77] = isMobile;
    $[78] = isOrgSettingsActive;
    $[79] = isSASettingsActive;
    $[80] = isTeamSettingsActive;
    $[81] = mounted;
    $[82] = navigationUpdatesEnabled;
    $[83] = onClose;
    $[84] = onNavigate;
    $[85] = organizationId;
    $[86] = saSettingsHref;
    $[87] = scrollContainerRef;
    $[88] = selectedKey;
    $[89] = showGoToDc1;
    $[90] = showThemeToggle;
    $[91] = t;
    $[92] = theme;
    $[93] = toggleChat;
    $[94] = toggleDrawer;
    $[95] = toggleTheme;
    $[96] = unreadCount;
    $[97] = unreadCountLabel;
    $[98] = t34;
  } else {
    t34 = $[98];
  }
  let t35;
  if ($[99] !== t10 || $[100] !== t31 || $[101] !== t32 || $[102] !== t34) {
    t35 = <div className={t10}>{t31}{t32}{t33}{t34}</div>;
    $[99] = t10;
    $[100] = t31;
    $[101] = t32;
    $[102] = t34;
    $[103] = t35;
  } else {
    t35 = $[103];
  }
  let t36;
  if ($[104] !== isOpen || $[105] !== onOpenChange || $[106] !== sidebarBreakpoint || $[107] !== t35 || $[108] !== t7) {
    t36 = <SidebarDrawer className={t7} hideCloseButton={true} isOpen={isOpen} sidebarBreakpoint={sidebarBreakpoint} onOpenChange={onOpenChange}>{t35}</SidebarDrawer>;
    $[104] = isOpen;
    $[105] = onOpenChange;
    $[106] = sidebarBreakpoint;
    $[107] = t35;
    $[108] = t7;
    $[109] = t36;
  } else {
    t36 = $[109];
  }
  return t36;
};
function _temp() {
  return window.open(getDcOneArchiveUrl(), "_blank", "noopener,noreferrer");
}
function _temp2() {
  return window.open(getDcOneArchiveUrl(), "_blank", "noopener,noreferrer");
}